import { useLayoutEffect, useRef } from "react";

interface FitContentWidthOptions {
    fitHeight?: boolean;
}

/** Fit typography using --fitted-content-scale. Fixed-height banners can also
 * constrain tall localized font metrics; wrapped blocks default to width only. */
export function useFitContentWidth(
    contentKey: string,
    { fitHeight = false }: FitContentWidthOptions = {},
) {
    const containerRef = useRef<HTMLSpanElement>(null);
    const contentRef = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content) return;
        let disposed = false;

        const fit = () => {
            if (disposed) return;
            // Always measure at the design size so a wider container or shorter
            // translation restores the original type. Layout widths ignore the
            // card's entrance, press, and progression transforms.
            content.style.setProperty("--fitted-content-scale", "1");
            const style = getComputedStyle(container);
            const availableWidth =
                container.clientWidth -
                parseFloat(style.paddingLeft) -
                parseFloat(style.paddingRight);
            const naturalWidth = content.scrollWidth;
            if (availableWidth <= 0 || naturalWidth === 0) return;

            const availableHeight =
                container.clientHeight -
                parseFloat(style.paddingTop) -
                parseFloat(style.paddingBottom);
            const heightScale =
                fitHeight && availableHeight > 0 && content.scrollHeight > 0
                    ? availableHeight / content.scrollHeight
                    : 1;
            const scale = Math.min(
                1,
                availableWidth / naturalWidth,
                heightScale,
            );
            content.style.setProperty("--fitted-content-scale", String(scale));
        };

        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(container);
        window.addEventListener("resize", fit);
        document.fonts.addEventListener("loadingdone", fit);
        void document.fonts.ready.then(fit);

        return () => {
            disposed = true;
            observer.disconnect();
            window.removeEventListener("resize", fit);
            document.fonts.removeEventListener("loadingdone", fit);
        };
    }, [contentKey, fitHeight]);

    return { containerRef, contentRef };
}
